import { Mic, Close } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  keyframes,
  CircularProgress,
} from "@mui/material";
import { Call } from "@vapi-ai/web/dist/api";
import { useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { eximiusChatUrl } from "../../api/agent";
import Vapi from "@vapi-ai/web";

const apiKey = process.env.REACT_APP_VAPI_KEY ?? "";
const vapi = new Vapi(apiKey);

interface VoiceSupportLightboxProps {
  onClose: () => void;
  apiResponse: any;
  selectedCandidate: any;
}

const pulseAnimation = keyframes`
  0% { background-color: #007ef4; }
  25% { background-color:#89b9e6; }
  50% { background-color: #007ef4; }
  75% { background-color:#6292bf; }
  100% { background-color: #007ef4; }
`;

const VoiceSupportLightbox: React.FC<VoiceSupportLightboxProps> = ({
  onClose,
  apiResponse,
  selectedCandidate,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [callLoading, setCallLoading] = useState<boolean>(false);
  const [callInstance, setCallInstance] = useState<Call | null>();
  const [jobQuestions, setJobQuestions] = useState<{ [key: string]: any[] }>({});
  const [jobContent, setJobContent] = useState("");
  const [jobCompanyName, setJobCompanyName] = useState("");

  useEffect(() => {
    setJobQuestions(apiResponse[0]?.screening_questions);
    setJobContent(apiResponse[0]?.job_content);
    setJobCompanyName(apiResponse[0]?.job_company_name);
  }, [apiResponse]);

  useEffect(() => {
    if (vapi !== undefined) {
      vapi.on("call-start", () => {
        console.log("Call has started.");
      });

      vapi.on("call-end", () => {
        console.log("Call has ended.");
        if (callInstance) {
          vapi.stop();
          handleDisconnect();
        }
      });

      return () => {
        vapi.removeAllListeners("call-start");
        vapi.removeAllListeners("call-end");
      };
    }
  }, [vapi, callInstance]);

  const startAudio = () => {
    if (callLoading) {
      socket?.disconnect();
      vapi.stop();
      setCallLoading(false);
      setCallInstance(null);
      return;
    }
    if (!callInstance) {
      setCallLoading(true);
      connect_recruiterbot();
      return;
    }
    handleDisconnect();
  };

  const handleDisconnect = () => {
    vapi.stop();
    setCallLoading(false);
    setCallInstance(null);
    socket?.disconnect();
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  const handleClose = () => {
    vapi.stop();
    setCallLoading(false);
    setCallInstance(null);
    socket?.disconnect();
    onClose();
  };

  const connect_recruiterbot = () => {
    const tenant_id = process.env.REACT_APP_TENANT_ID ?? "";
    if (tenant_id) {
      const newSocket = io(`${eximiusChatUrl}/pg_recruiterbot`, {
        query: {
          tenant_id: tenant_id,
          mode: "audio",
        },
        transports: ["websocket"],
        reconnection: true, // Enable reconnection
        reconnectionAttempts: 5, // Limit reconnection attempts
      });

      // Set socket state
      setSocket(newSocket);

      // Set up socket listeners
      setupSocketListeners(newSocket);

      return () => {
        if (newSocket) {
          newSocket.disconnect();
        }
      };
    }
  };

  const setupSocketListeners = (socket: Socket) => {
    socket.on("connect", () => {
      console.log("Connected to server.");
    });

    socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
    });

    socket.on("disconnect", () => {
      setCallLoading(false);
    });

    socket.on("call_response", handleCallResponse);
  };

  const getInitialPrompt = async (assistantPrompt: any) => {
    let initialPrompt = assistantPrompt
      .replace("{screening_questions}", JSON.stringify(jobQuestions, null, 2)) // Convert array to string
      .replace("{job_content}", jobContent);

    initialPrompt = initialPrompt.replace(/{{/g, "{").replace(/}}/g, "}");

    return initialPrompt;
  };

  const getSystemPrompt = async (systemPrompt: any) => {
    let prompt = systemPrompt
      .replace("{company_name}", jobCompanyName)
      .replace("{firstname}", selectedCandidate?.firstName || "");
    return prompt;
  };

  const handleCallResponse = async (data: any) => {
    const initialPrompt = await getInitialPrompt(data["vapi_initial_prompt"]);
    const systemPrompt = await getSystemPrompt(data["vapi_system_prompt"]);
    const voice_settings = data["vapi_voice_settings"];
    const other_settings = data["vapi_other_settings"];

    const assistant: any = {
      model: {
        provider: other_settings["provider"],
        model: other_settings["model"],
        messages: [
          {
            role: "assistant",
            content: initialPrompt,
          },
          {
            role: "system",
            content: systemPrompt,
          },
        ],
      },
      voice: {
        provider: voice_settings["voiceProvider"],
        voiceId: voice_settings["voiceId"],
        speed: voice_settings["speed"],
        model: voice_settings["voiceModel"],
        stability: voice_settings["stability"],
        similarityBoost: voice_settings["similarityBoost"],
        fillerInjectionEnabled: voice_settings["fillerInjectionEnabled"]
      },
      transcriber: {
        provider: voice_settings["transcriberProvider"],
        model: voice_settings["transcriberModel"],
        language: voice_settings["transcriberLanguage"],
      },
      firstMessage: data["vapi_first_message"],
      analysisPlan: {
        summaryPrompt: data["vapi_summary_prompt"]!,
        structuredDataPrompt: data["vapi_structured_data_prompt"]!,
      },
      endCallFunctionEnabled: other_settings["endCallFunctionEnabled"],
      endCallPhrases: data["vapi_end_call_phrases"]!,
      clientMessages: other_settings["clientMessages"],
      serverMessages: other_settings["serverMessages"],
      silenceTimeoutSeconds: other_settings["silenceTimeoutSeconds"],
      maxDurationSeconds: other_settings["maxDurationSeconds"],
      backgroundSound: other_settings["backgroundSound"],
      backgroundDenoisingEnabled: other_settings["backgroundDenoisingEnabled"],
      modelOutputInMessagesEnabled:
        other_settings["modelOutputInMessagesEnabled"],
    };
    const call = await vapi.start(assistant);
    setCallLoading(false);
    setCallInstance(call);
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center" }}>
            Voice Screening
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "grey.600" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={2}
          pb={3}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            mb={2}
            textAlign="center"
          >
            {callInstance || callLoading
              ? "Call is in progress..."
              : "Please click on the icon to start the screening."}
          </Typography>
          <Box
            onClick={startAudio}
            sx={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: "1px solid #0066F4",
              backgroundColor: "#0066F4",
              animation: callInstance?.id
                ? `${pulseAnimation} 1.5s infinite alternate ease-in-out`
                : "none",
            }}
          >
            {callLoading ? (
              <CircularProgress sx={{ color: "primary" }} size={48} />
            ) : (
              <Mic sx={{ fontSize: 48, color: "#FFFFFF" }} />
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceSupportLightbox;
